import React, { useEffect, useState, useRef } from 'react';
import { Modal, Button, ProgressBar } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateFormData,
  setStepValidity,
  setCurrentStep,
  loadFromLocalStorage,
  resetOnboarding
} from '../../redux/onboarding/onboardingSlice';
import StepProfileBasics from './steps/StepProfileBasics';
import StepProfileOptional from './steps/StepProfileOptional';
import StepDynamicQuestions from './steps/StepDynamicQuestions';
import StepFinalization from './steps/StepFinalization';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import endpoints from '../../api/endpoints';
import { updateUserProfile, saveProfileImage, saveLocation } from '../../services/UserService';
import { setNextStep } from '../../services/onboardingApi';

const steps = [
  { key: 'STEP_1_PROFILE_BASICS', component: StepProfileBasics, label: 'Basisdaten' },
  { key: 'STEP_2_PROFILE_OPTIONAL', component: StepProfileOptional, label: 'Profilinformationen' },
  { key: 'STEP_3_DYNAMIC_QUESTIONS', component: StepDynamicQuestions, label: 'Fragen' },
  { key: 'STEP_4_FINALIZATION', component: StepFinalization, label: 'Fertigstellen' },
];

const OnboardingModal = ({ show, onComplete }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { formData, currentStep, stepValidity } = useSelector((state) => state.onboarding);
  const StepComponent = steps[currentStep]?.component;

  const prevFormDataRef = useRef(formData);

  // Load step from backend first
  useEffect(() => {
    if(!user || !user.currentOnbaordingStep) return;
    console.log("OnboardingModal user", user);

    const realOnboardingStep = steps.findIndex((step) => step.key === user.currentOnbaordingStep);
    
    if (realOnboardingStep >= 0) {
      dispatch(setCurrentStep(realOnboardingStep));
    }

  }, [user, dispatch]);

  useEffect(() => {
    // Load from localStorage only if backend step is not present
    if (user?.currentOnboardingStep) return;

    const savedState = localStorage.getItem('onboardingState');

    console.log(steps[currentStep].key + " useEffect", formData, currentStep, stepValidity);
    console.log(steps[currentStep].key + " useEffect onboardingState", JSON.parse(savedState));

    if (savedState && user?.currentOnbaordingStep === undefined) {
      try {
        const parsedState = JSON.parse(savedState);
        dispatch(loadFromLocalStorage(parsedState));
      } catch (err) {
        console.warn('Invalid saved onboarding state', err);
        localStorage.removeItem('onboardingState');
      }
    }
  }, [dispatch, user]);

  // Safety check: fallback to step 0 if index is invalid
  useEffect(() => {
    if (currentStep >= steps.length) {
      dispatch(setCurrentStep(0)); // fallback to step 0
    }
  }, [currentStep]);

  // Save onboarding state to localStorage
  useEffect(() => {
    const stateToSave = { formData, currentStep, stepValidity };
    if (Object.keys(formData).length || currentStep || Object.keys(stepValidity).length) {
      localStorage.setItem('onboardingState', JSON.stringify(stateToSave));
    }
  }, [formData, currentStep, stepValidity]);

  const handleNext = async () => {
    if (!user) return;

    console.log({
      user: user,
      currentStep: currentStep,
      formData: formData,
      stepsLength: steps.length,
    });

    try {
      // Save profile data and image
      //const isFormDataDirty = JSON.stringify(formData ?? {}) !== JSON.stringify(prevFormDataRef.current ?? {});
      //if (isFormDataDirty) {
        await handleProfileUpdate();
      //  console.log(steps[currentStep].key + " handleProfileUpdate formData", formData);
      //  prevFormDataRef.current = { ...formData}; // ✅ update only after saving && avoid reference assignment
      //}

      if (currentStep === 0 && formData?.lat && formData?.lng) {
        await handleLocationSave();
      }

      if (currentStep === 1 && formData?.profilePhoto && formData?.profilePhoto instanceof File) {
        await handleProfileImageUpload();
      }

      if(currentStep === steps.length - 1) handleFinalization();
  
      await moveNextStep();

    } catch (error) {
      console.log("error", error);
      toast.error("Fehler beim Speichern. Bitte erneut versuchen.");
    }
  };

  const moveNextStep = async() => {
    const nextStepIndex = currentStep < steps.length - 1 ? currentStep + 1 : currentStep;
    const stepPayload = {
      step: nextStepIndex === steps.length - 1 ? 'COMPLETED' : steps[nextStepIndex].key,
    }
    console.log("####################" + steps[currentStep].key + " moveNextStep stepPayload", stepPayload);
    dispatch(setCurrentStep(nextStepIndex));
    console.log("")
    await setNextStep(stepPayload);
  };

  const handleProfileUpdate = async () => {
    console.log(steps[currentStep].key + " handleProfileUpdate currentStep", currentStep);
    console.log(steps[currentStep].key + " handleProfileUpdate formData", formData);
    let profileUpdatePayload = {};
    switch (currentStep) {
      case 0:
        console.log(steps[currentStep].key + " handleProfileUpdate swicth-case", 1);
        profileUpdatePayload = {
          displayName: formData.displayName
        }
        break;

      case 1:
        console.log(steps[currentStep].key + " handleProfileUpdate swicth-case", 2);
        profileUpdatePayload = {
          bio: formData.bio,
          jobTitle: formData.jobTitle,
          educationLevel: formData.educationLevel,
        }
        break;

      case 3:
        console.log(steps[currentStep].key + " handleProfileUpdate swicth-case", 4);
        profileUpdatePayload = {
          agb: formData.agb,
          privacy: formData.privacy,
          marketingConsent: formData.marketingConsent,
          phoneContactConsent: formData.phoneContactConsent,
          communityRulesConsent: formData.communityRulesConsent,
          profileVisibilityConsent: formData.profileVisibilityConsent
        }
        break;

      default:
        return;
    }
    //Update User 
    console.log(steps[currentStep].key + " handleProfileUpdate profileUpdatePayload", profileUpdatePayload);
    try {
      const profileUpdated = await updateUserProfile(profileUpdatePayload);
      console.log(steps[currentStep].key + " handleProfileUpdate profileUpdated", profileUpdated);
      if (profileUpdated?.status === 200) {
        toast.success('Profilinformationen gespeichert.');
      } else {
        toast.error('Fehler beim Profil Update.');
      }
    } catch (error) {
      toast.error('Unknown error on updating user profile');
      console.log(steps[currentStep].key + " handleProfileUpdate error", error);
    }

  };

  const handleProfileImageUpload = async ()=> {
    // Save User Profile Image
    try {
      if (currentStep === 1 && formData?.profilePhoto && formData?.profilePhoto instanceof File) {
        const imageForm = new FormData();
        imageForm.append('file', formData.profilePhoto);
        await saveProfileImage(imageForm);
        toast.success('Profilbild hochgeladen.');
      }
    } catch (error) {
      toast.error('Fehler beim komplett Step Update');
      //throw error; // Propagate error to handleNext
    }
  };

  const handleLocationSave = async () => {
    //Save Location
    try {
      const locationPostReqBody = {
        "latitude": formData.lat,
        "longitude": formData.lng
      }

      const locationSaved = await saveLocation(locationPostReqBody)
      if (locationSaved?.status === 200) {
        toast.success('Lokation gespeichert.');
      } else {
        toast.error('Fehler beim Speichern des Benutzerstandorts');
      }
    } catch (error) {
      toast.error('Unbekannter Fehler beim Speichern des Benutzerstandorts');
    }
  };

  const handleFinalization = () => {
    // Clear the localStorage and reset the onboarding state in Redux
    localStorage.removeItem('onboardingState');
    dispatch(resetOnboarding());
    toast.success('Onboarding abgeschlossen!');
    onComplete(); // Notify parent component (if required)
  };

  const isNextEnabled = stepValidity[currentStep] === true;

  return (
    <Modal className='onboarding-modal' show={show} size="lg" centered backdrop="static">
      <Modal.Header>
        <Modal.Title>Onboarding – Schritt {currentStep + 1} von {steps.length}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {StepComponent && (
          <StepComponent
            data={formData}
            setData={(updateData) => dispatch(updateFormData(updateData))}
            setStepValidity={(valid) => dispatch(setStepValidity({ stepIndex: currentStep, isValid: valid }))}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <ProgressBar
          now={((currentStep + 1) / steps.length) * 100}
          className="w-100 mb-3"
          variant="info"
        />
        <div className="d-flex w-100 justify-content-end">
          {/*
          <Button variant="secondary hidden" onClick={handleBack} disabled={currentStep === 0}>
            Zurück
          </Button>
          */}
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!isNextEnabled}
          >
            {currentStep === steps.length - 1 ? 'Abschließen' : 'Weiter'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// Helper to map formData to final backend payload
function buildFinalPayload(data) {
  return {
    eyeColor: data.eyeColor || '',
    hairColor: data.hairColor || '',
    weight: Number(data.weight) || 0,
    height: Number(data.height) || 0,
    religion: data.religion || '',
    location: data.location || '',
    userType: 1,
    answers: Array.isArray(data.answers) ? data.answers : [],
    profilePhoto: data.profilePhotoBase64 || null,
    gallery: Array.isArray(data.galleryBase64) ? data.galleryBase64 : [],
    bio: data.bio || '',
    username: data.username || '',
    marketingConsent: !!data.marketingConsent,
    phoneContactConsent: !!data.phoneContactConsent,
    communityRulesConsent: !!data.communityRulesConsent,
  };
}

export default OnboardingModal;
